import React from "react";
import { Head } from "@inertiajs/react";
import { Grid, Stack, Group, Button } from "@mantine/core";
import PageShell from "../../Components/Layout/PageShell";
import PageBackgroundPhoto from "../../Components/Layout/PageBackgroundPhoto";
import { Heading1, DimmedText, BodyText } from "../../Components/UI/Typography";
import { Card } from "../../Components/UI/Card";
import { Badge } from "../../Components/UI/Badge";
import { PostCard } from "../../Components/PostCard";
import { LatestPostsStrip } from "../../Components/LatestPostsStrip";
import { getPostsByPillar } from "../../data/posts";

export default function ThinkIndex() {
  const [activeTag, setActiveTag] = React.useState<string | null>(null);

  const thinkPosts = getPostsByPillar("think");
  const allTags = Array.from(
    new Set(thinkPosts.flatMap((p) => p.tags)),
  ).sort();
  const posts = activeTag
    ? thinkPosts.filter((p) => p.tags.includes(activeTag))
    : thinkPosts;

  return (
    <>
      <Head>
        <title>Think | Ogarocious</title>
        <meta
          name="description"
          content="Blog, essays, newsletter, and writing — on code, creativity, community, and the intersections between them."
        />
        <meta property="og:title" content="Think | Ogarocious" />
        <meta
          property="og:description"
          content="Blog, essays, newsletter, and writing — on code, creativity, community, and the intersections between them."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${import.meta.env.VITE_SITE_URL || ""}/think`}
        />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Think | Ogarocious" />
        <meta
          name="twitter:description"
          content="Blog, essays, newsletter, and writing."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell
        variant="content"
        extraBackgrounds={
          <PageBackgroundPhoto src={import.meta.env.VITE_THINK_PHOTO_URL} />
        }
      >
        <Stack gap={48}>
          {/* ── Header ── */}
          <Stack gap="md" align="center">
            <Heading1 variant="shadow">Think</Heading1>
            <DimmedText
              size="lg"
              style={{
                textAlign: "center",
                maxWidth: "clamp(280px, 90vw, 520px)",
              }}
            >
              Writing on code, creativity, community building, and the
              intersections between them.
            </DimmedText>
          </Stack>

          {/* ── Bio ── */}
          <Card padding="xl" radius="md">
            <Stack gap="md">
              <BodyText>
                The thread across tech, dance, and media is systems thinking —
                but the writing goes deeper than that. I write about race and
                Black identity, the exploitation of Black creative labor,
                intentional relationships, the philosophy of existence,
                communication theory, and what community organizing reveals
                about human nature. The questions I&apos;m most interested in
                tend to sit at the intersection of culture, systems, and self.
              </BodyText>
              <BodyText>
                The style is honest and unfiltered — stream of consciousness
                that lands somewhere analytical. I&apos;ve been writing these
                reflections publicly for years, first as Facebook posts and
                newsletters, now here. If you want to understand how all the
                other work connects, this is the place.
              </BodyText>
            </Stack>
          </Card>

          {/* ── Tag Filters ── */}
          {allTags.length > 0 && (
            <Group justify="center" gap="xs" wrap="wrap">
              <Button
                variant={activeTag === null ? "gradient" : "subtle"}
                color={activeTag === null ? undefined : "gray"}
                size="xs"
                radius="xl"
                onClick={() => setActiveTag(null)}
              >
                All
              </Button>
              {allTags.map((tag) => (
                <Badge
                  key={tag}
                  variant={activeTag === tag ? "tools" : "light"}
                  size="lg"
                  radius="xl"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setActiveTag(activeTag === tag ? null : tag)
                  }
                >
                  {tag}
                </Badge>
              ))}
            </Group>
          )}

          {/* ── Posts Grid ── */}
          {posts.length > 0 ? (
            <Grid>
              {posts.map((post) => (
                <Grid.Col
                  key={post.slug}
                  span={{ base: 12, sm: 6, lg: 4 }}
                >
                  <PostCard post={post} />
                </Grid.Col>
              ))}
            </Grid>
          ) : (
            <Stack align="center" py={60} gap="md">
              <DimmedText size="lg">
                No posts found for &ldquo;{activeTag}&rdquo;.
              </DimmedText>
              <Button
                variant="subtle"
                color="teal"
                onClick={() => setActiveTag(null)}
              >
                Clear filter
              </Button>
            </Stack>
          )}

          {/* ── Related from other pillars (cross-pillar posts only —
               primary think posts already fill the grid above) ── */}
          <LatestPostsStrip pillar="think" crossPillarOnly />
        </Stack>
      </PageShell>
    </>
  );
}
